import { useEffect } from 'react';
import { initTelegram } from './lib/telegram';
import { SessionProvider, useSession } from './lib/session';
import { Route, RouterProvider, useRouter } from './lib/router';
import { I18nProvider, useT } from './lib/i18n';
import { BrandMark, Wordmark } from './components/Brand';
import { BottomNav } from './components/BottomNav';
import { OfflineBanner } from './components/OfflineBanner';
import { ToastProvider } from './components/Toast';

import { OnboardingPage } from './pages/Onboarding';
import { HomePage } from './pages/Home';
import { SearchResultsPage } from './pages/SearchResults';
import { RideDetailPage } from './pages/RideDetail';
import { EditRidePage } from './pages/EditRide';
import { PublishPage } from './pages/Publish';
import { MyRidesPage } from './pages/MyRides';
import { MyRidesArchivePage } from './pages/MyRidesArchive';
import { MyTemplatesPage } from './pages/MyTemplates';
import { MyBookingsPage } from './pages/MyBookings';
import { BookingDetailPage } from './pages/BookingDetail';
import { ProfilePage } from './pages/Profile';
import { PublicProfilePage } from './pages/PublicProfile';
import { PremiumPage } from './pages/Premium';
import { LeaderboardPage } from './pages/Leaderboard';
import { LegalPage } from './pages/Legal';
import { SharedTripPage } from './pages/SharedTrip';

export function App(): JSX.Element {
  useEffect(() => {
    initTelegram();
  }, []);

  return (
    <RouterProvider>
      <Root />
    </RouterProvider>
  );
}

function Root(): JSX.Element {
  const { path } = useRouter();
  // Public safety share page — no Telegram session required (family/friends).
  if (path.startsWith('/s/')) {
    return (
      <I18nProvider>
        <SharedTripPage token={decodeURIComponent(path.slice('/s/'.length))} />
      </I18nProvider>
    );
  }
  return (
    <SessionProvider>
      <I18nGate>
        <ToastProvider>
          <OfflineBanner />
          <SessionGate>
            <Routes />
          </SessionGate>
        </ToastProvider>
      </I18nGate>
    </SessionProvider>
  );
}

function I18nGate({ children }: { children: React.ReactNode }): JSX.Element {
  const { me } = useSession();
  return <I18nProvider serverLocale={me?.locale}>{children}</I18nProvider>;
}

const BOT_USERNAME = (import.meta.env.VITE_BOT_USERNAME as string | undefined) ?? 'WayloRide_bot';

function SessionGate({ children }: { children: React.ReactNode }): JSX.Element {
  const { status, me, error, diagnostic, refresh } = useSession();
  const { navigate, path } = useRouter();
  const { t } = useT();

  const isOutsideTelegram = !window.Telegram?.WebApp?.initData;
  const isDevAuthBlocked = error?.toLowerCase().includes('dev auth') ?? false;

  useEffect(() => {
    // Push first-launch users to onboarding once authenticated.
    if (status === 'authenticated' && me && !me.onboardedAt && !path.startsWith('/onboarding')) {
      navigate('/onboarding');
    }
  }, [status, me, path, navigate]);

  if (status === 'loading') return <LoadingSplash />;
  if (status === 'error') {
    // Out-of-Telegram + production = the dev-auth bypass is closed. Show a
    // helpful "Open in Telegram" screen instead of a raw server error.
    if (isOutsideTelegram && isDevAuthBlocked) {
      return <OpenInTelegramSplash />;
    }
    return (
      <main className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center gap-4 px-6 text-center">
        <BrandMark size={64} />
        <Wordmark />
        <p className="text-sm text-destructive">
          {t('app.errorPrefix')} {error}
        </p>
        {diagnostic && (
          <details className="w-full max-w-xs text-left text-xs text-tg-hint">
            <summary className="cursor-pointer">Diagnostic info</summary>
            <pre className="mt-2 overflow-x-auto rounded-input bg-tg-bg-secondary p-2">
{`In Telegram:  ${diagnostic.inTelegram}
initData len: ${diagnostic.initDataLength}
TG version:   ${diagnostic.tgVersion ?? '—'}
TG platform:  ${diagnostic.tgPlatform ?? '—'}`}
            </pre>
          </details>
        )}
        <button
          type="button"
          onClick={() => void refresh()}
          className="rounded-input bg-brand-green px-4 py-2 text-sm font-semibold text-white"
        >
          {t('app.retry')}
        </button>
      </main>
    );
  }
  return <>{children}</>;
}

function OpenInTelegramSplash(): JSX.Element {
  const { t } = useT();
  const botUrl = `https://t.me/${BOT_USERNAME}`;
  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center gap-5 px-6 text-center">
      <BrandMark size={72} />
      <Wordmark />
      <p className="text-sm text-tg-hint">{t('app.tagline')}</p>
      <div className="rounded-card bg-tg-bg-secondary p-5">
        <p className="text-base font-semibold text-tg-text">
          {t('app.openInTelegram.title')}
        </p>
        <p className="mt-1 text-sm text-tg-hint">{t('app.openInTelegram.body')}</p>
      </div>
      <a
        href={botUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="rounded-input bg-brand-green px-5 py-3 text-sm font-semibold text-white"
      >
        {t('app.openInTelegram.cta')}
      </a>
      <p className="text-xs text-tg-hint">@{BOT_USERNAME}</p>
    </main>
  );
}

function LoadingSplash(): JSX.Element {
  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center gap-3">
      <BrandMark size={64} />
      <Wordmark />
      <p className="text-sm text-tg-hint">Connecting…</p>
    </main>
  );
}

function Routes(): JSX.Element {
  const { path } = useRouter();
  const { me } = useSession();

  // If onboarding not done yet, force the onboarding screen.
  if (me && !me.onboardedAt) {
    return <OnboardingPage />;
  }

  // Strip query string when matching
  const pathOnly = (path.split('?')[0] ?? '/') || '/';

  return (
    <>
      <Route pattern="/">{() => <HomePage />}</Route>
      <Route pattern="/onboarding">{() => <OnboardingPage />}</Route>
      <Route pattern="/search/results">{() => <SearchResultsPage />}</Route>
      <Route pattern="/ride/:id">{(p) => <RideDetailPage rideId={p.id!} />}</Route>
      <Route pattern="/ride/:id/edit">{(p) => <EditRidePage rideId={p.id!} />}</Route>
      <Route pattern="/publish">{() => <PublishPage />}</Route>
      <Route pattern="/my/rides">{() => <MyRidesPage />}</Route>
      <Route pattern="/my/rides/archive">{() => <MyRidesArchivePage />}</Route>
      <Route pattern="/my/templates">{() => <MyTemplatesPage />}</Route>
      <Route pattern="/my/bookings">{() => <MyBookingsPage />}</Route>
      <Route pattern="/booking/:id">{(p) => <BookingDetailPage bookingId={p.id!} />}</Route>
      <Route pattern="/profile">{() => <ProfilePage />}</Route>
      <Route pattern="/profile/:userId">{(p) => <PublicProfilePage userId={p.userId!} />}</Route>
      <Route pattern="/premium">{() => <PremiumPage />}</Route>
      <Route pattern="/leaderboard">{() => <LeaderboardPage />}</Route>
      <Route pattern="/privacy">{() => <LegalPage variant="privacy" />}</Route>
      <Route pattern="/terms">{() => <LegalPage variant="terms" />}</Route>
      {/* Fallback: if no route matched (rare), show home. */}
      {!KNOWN_ROUTES.some((r) => matchesPattern(r, pathOnly)) && <HomePage />}
      <BottomNav />
    </>
  );
}

const KNOWN_ROUTES = [
  '/',
  '/onboarding',
  '/search/results',
  '/ride/:id',
  '/ride/:id/edit',
  '/publish',
  '/my/rides',
  '/my/rides/archive',
  '/my/templates',
  '/my/bookings',
  '/booking/:id',
  '/profile',
  '/profile/:userId',
  '/premium',
  '/leaderboard',
  '/privacy',
  '/terms',
];

function matchesPattern(pattern: string, path: string): boolean {
  const pSeg = pattern.replace(/^\//, '').split('/').filter(Boolean);
  const aSeg = path.replace(/^\//, '').split('/').filter(Boolean);
  if (pSeg.length !== aSeg.length) return false;
  for (let i = 0; i < pSeg.length; i++) {
    const p = pSeg[i]!;
    const a = aSeg[i]!;
    if (!p.startsWith(':') && p !== a) return false;
  }
  return true;
}
